import { useEffect, useMemo, useState, type FormEvent } from 'react'
import {
  Alert,
  Box,
  Button,
  Chip,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Switch,
  TextField,
  Typography,
} from '@mui/material'
import { getUserById, perfilLabel, updateUser } from '../api/users'
import { maskWhatsapp, whatsappValido } from '../utils/whatsapp'
import { useBiAuth } from '../state/BiAuthContext'
import { useBiTheme } from '../state/BiThemeContext'
import { THEME_OPTIONS, type BiThemeId } from '../theme'

export function PerfilPage() {
  const { user, updateUser: updateAuthUser } = useBiAuth()
  const { themeId, setThemeId } = useBiTheme()

  const [nome, setNome] = useState('')
  const [email, setEmail] = useState('')
  const [whatsapp, setWhatsapp] = useState('')
  const [ativo, setAtivo] = useState(true)
  const [senha, setSenha] = useState('')
  const [senhaConfirm, setSenhaConfirm] = useState('')
  const [temaVisual, setTemaVisual] = useState<BiThemeId>(themeId)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')

  const labelPerfil = useMemo(() => perfilLabel(user?.tipo), [user?.tipo])

  // O WhatsApp não vem na sessão: busca o registo do próprio utilizador.
  const [whatsappSalvo, setWhatsappSalvo] = useState('')
  useEffect(() => {
    if (!user?.id) return
    let cancelado = false
    getUserById(user.id)
      .then((u) => {
        if (cancelado) return
        const m = maskWhatsapp(u.whatsapp || '')
        setWhatsappSalvo(m)
        setWhatsapp(m)
      })
      .catch(() => {})
    return () => {
      cancelado = true
    }
  }, [user?.id])

  useEffect(() => {
    if (!user) return
    setNome(user.nome || '')
    setEmail(user.email || '')
    setAtivo(true)
    setSenha('')
    setSenhaConfirm('')
    setTemaVisual(themeId)
  }, [user, themeId])

  const handleCancel = () => {
    if (!user) return
    setNome(user.nome || '')
    setEmail(user.email || '')
    setWhatsapp(whatsappSalvo)
    setAtivo(true)
    setSenha('')
    setSenhaConfirm('')
    setTemaVisual(themeId)
    setError('')
  }

  const handleSave = async (event: FormEvent) => {
    event.preventDefault()
    if (!user) return
    if (!nome.trim()) {
      setError('Nome é obrigatório')
      return
    }
    if (!whatsappValido(whatsapp)) {
      setError('WhatsApp inválido. Use DDD + número')
      return
    }
    if (senha && senha !== senhaConfirm) {
      setError('Nova senha e confirmação não coincidem')
      return
    }
    if (!senha && senhaConfirm) {
      setError('Preencha a nova senha ou limpe a confirmação')
      return
    }
    try {
      setLoading(true)
      setError('')
      const payload: {
        nome: string
        email: string
        whatsapp: string
        ativo: boolean
        senha?: string
      } = {
        nome: nome.trim(),
        email: email.trim(),
        whatsapp,
        ativo,
      }
      if (senha.trim()) payload.senha = senha.trim()
      const updated = await updateUser(user.id, payload)
      updateAuthUser({
        id: updated.id,
        email: updated.email,
        nome: updated.nome,
        tipo: updated.tipo,
      })
      const m = maskWhatsapp(updated.whatsapp || '')
      setWhatsappSalvo(m)
      setWhatsapp(m)
      setSenha('')
      setSenhaConfirm('')
      setThemeId(temaVisual)
      setSuccess('Dados da conta atualizados com sucesso')
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Erro ao salvar conta')
    } finally {
      setLoading(false)
    }
  }

  return (
    <Box>
      <Typography variant="h4" component="h1" sx={{ mb: 0.5 }}>
        Minha conta
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Atualize seus dados de acesso. O perfil só pode ser alterado por outro administrador em
        Usuários.
      </Typography>

      <Paper elevation={1} sx={{ p: 3, borderRadius: 2 }}>
        <Typography variant="h6" sx={{ mb: 1.5, fontWeight: 700 }}>
          Dados da conta
        </Typography>
        {error ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        ) : null}
        <Box component="form" onSubmit={(e) => void handleSave(e)}>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: 'repeat(4, minmax(0, 1fr))' },
              gap: 1.5,
              alignItems: 'end',
            }}
          >
            <TextField
              label="Usuário"
              required
              value={nome}
              onChange={(e) => setNome(e.target.value)}
              disabled={loading}
              size="small"
              fullWidth
            />
            <TextField
              label="Email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={loading}
              size="small"
              fullWidth
            />
            <TextField
              label="Nova senha"
              type="password"
              value={senha}
              onChange={(e) => setSenha(e.target.value)}
              disabled={loading}
              size="small"
              fullWidth
            />
            <TextField
              label="Confirmar nova senha"
              type="password"
              value={senhaConfirm}
              onChange={(e) => setSenhaConfirm(e.target.value)}
              disabled={loading}
              size="small"
              fullWidth
            />
          </Box>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{
              display: 'block',
              mt: 0.75,
              ml: { xs: 0, md: '50%' },
              pl: { xs: 0, md: '8px' },
            }}
          >
            Deixe em branco para manter a senha atual. Mínimo 6 caracteres.
          </Typography>
          <Box
            sx={{
              mt: 1.25,
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: 'repeat(4, minmax(0, 1fr))' },
              gap: 1.5,
              alignItems: 'end',
            }}
          >
            <TextField
              label="WhatsApp (opcional)"
              placeholder="(92) 99999-9999"
              value={whatsapp}
              onChange={(e) => setWhatsapp(maskWhatsapp(e.target.value))}
              disabled={loading}
              size="small"
              fullWidth
              autoComplete="off"
              inputProps={{ inputMode: 'tel', maxLength: 15 }}
            />
          </Box>
          <Box
            sx={{
              mt: 1.25,
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: 'repeat(4, minmax(0, 1fr))' },
              gap: 1.5,
              alignItems: 'end',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                minHeight: 40,
              }}
            >
              <Typography variant="caption" color="text.secondary">
                Perfil
              </Typography>
              <Chip label={labelPerfil} color="primary" size="small" />
            </Box>
            <FormControl size="small" fullWidth>
              <InputLabel id="me-theme-label">Escolha o Tema Visual</InputLabel>
              <Select
                labelId="me-theme-label"
                value={temaVisual}
                label="Escolha o Tema Visual"
                onChange={(e) => setTemaVisual(e.target.value as BiThemeId)}
                disabled={loading}
              >
                {THEME_OPTIONS.map((opt) => (
                  <MenuItem key={opt.id} value={opt.id}>
                    {opt.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Box sx={{ display: 'flex', alignItems: 'center', minHeight: 40 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={ativo}
                    onChange={(e) => setAtivo(e.target.checked)}
                    disabled={loading}
                  />
                }
                label="Conta ativa"
                sx={{ mr: 0 }}
              />
            </Box>
            <Box sx={{ display: 'flex', gap: 1, justifyContent: { xs: 'flex-start', md: 'flex-end' } }}>
              <Button type="submit" variant="contained" disabled={loading}>
                Salvar
              </Button>
              <Button type="button" variant="outlined" onClick={handleCancel} disabled={loading}>
                Cancelar
              </Button>
            </Box>
          </Box>
        </Box>
      </Paper>

      <Snackbar
        open={!!success}
        autoHideDuration={2500}
        onClose={() => setSuccess('')}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert onClose={() => setSuccess('')} severity="success">
          {success}
        </Alert>
      </Snackbar>
    </Box>
  )
}
